//#region Util Imports

import * as _ from 'underscore'
import * as moment from 'moment'

//#endregion

//#region DevExtreme Imports

import DevExpress from "devextreme";
import dxList, { dxListItem, dxListOptions } from 'devextreme/ui/list';
import dxCalendar, { dxCalendarOptions } from 'devextreme/ui/calendar';
import dxButton, { dxButtonOptions } from 'devextreme/ui/button';

//#endregion

import { IIMRPopup, IMRPopupOptions } from '@imr/controls.angularjs/components/overlay/popup';
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base';

import { ConfigHelper, LocalizationHelper, TemplateHelper } from "../../../helpers";
import { UserSessionService } from "../../../services/user-session";
import { IMRBaseControlController } from "../base";
import { DateRange, DateRangeSelector, DateRangeSelectorOptions } from './date-range-selector.interfaces';
import { Period } from './date-range-selector.interfaces';

export class DateRangeSelectorController
    extends
    IMRBaseControlController<DateRangeSelector, DateRangeSelectorOptions>
    implements
    DateRangeSelector {
    public get popupRootElementId(): string { return 'showpopuphere' }
    public periodListOptions: dxListOptions
    public dateFromCalendarOptions: dxCalendarOptions
    public dateToCalendarOptions: dxCalendarOptions
    public popupOptions: IMRPopupOptions
    public periodChangeButtonOptions: dxButtonOptions
    public prevButtonOptions: dxButtonOptions
    public nextButtonOptions: dxButtonOptions
    public selectedPeriod: dxListItem


    private popup: IIMRPopup
    private periodChangeButton: dxButton
    private periodList: dxList
    private dateToCalendar: dxCalendar
    private dateFromCalendar: dxCalendar

    private dateToCalendarDisabled: boolean
    private dateFromCalendarDisabled: boolean
    private dateFromCalendarValue: Date
    private dateToCalendarValue: Date
    private listItems: dxListItem[]

    private waitAfterDateChange: boolean
    private dateRangeSelectionTimeout: any

    private periods: Period = {
        day: LocalizationHelper.getLocalizedText("Day"),
        week: LocalizationHelper.getLocalizedText("Week"),
        month: LocalizationHelper.getLocalizedText("Month"),
        year: LocalizationHelper.getLocalizedText("Year"),
        custom: LocalizationHelper.getLocalizedText("Custom"),
        all: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.All)
    }

    private popupWidth: number = 800
    private popupHeight: number = 350


    // Ctor

    constructor(
        private readonly userSession: UserSessionService
    ) {
        super()
    }

    // Ctor End   

    //#region Component    

    protected initialize(options: DateRangeSelectorOptions): void {
        this.initializePeriodList()
        this.initializeDateFromCalendar()
        this.initializeDateToCalendar()
        this.initializePopup()
        this.initializePeriodChangeButtonOptions()
        this.initializePrevButtonOptions()
        this.initializeNextButtonOptions()
    }

    //#endregion

    //#region Events

    private onDateRangeChanged(): void {
        if (this.dateRangeSelectionTimeout != null) {
            clearTimeout(this.dateRangeSelectionTimeout)
        }

        this.dateRangeSelectionTimeout = setTimeout(() => {
            if (_.isFunction(this.options.onDateRangeChanged)) {
                this.options.onDateRangeChanged(this.dateFromCalendarValue, this.dateToCalendarValue)
            }
        }, 1000)

    }
    //#endregion

    public getDateRange(): DateRange {
        return { dateFrom: this.dateFromCalendarValue, dateTo: this.dateToCalendarValue }

    }

    private clear(): void {

    }

    private initializePeriodList(): JQueryPromise<any> {
        this.listItems = _.map(_.values(this.periods), (value: string) => { return <dxListItem>{ text: value } })
        this.selectedPeriod = _.find(this.listItems, (item: dxListItem) => item.text == this.periods.all)
        this.periodListOptions = {
            items: this.listItems,
            hoverStateEnabled: true,
            selectionMode: 'single',
            activeStateEnabled: true,
            bindingOptions: {
                selectedItem: '$ctrl.selectedPeriod',
            },

            onItemClick: (e: any) => {

            },
            onItemRendered: e => {

            },
            onSelectionChanged: (e: any) => {
                if (_.size(e.addedItems) > 0) {
                    this.updateSelectPeriodButtonText(e.addedItems[0].text)
                    this.updateDateSelection(null, -1)
                }
            },
            onInitialized: (e: any) => {
                this.periodList = e.component
            }
        }

        return super.waitForOnInitialized(this.periodListOptions)
    }

    private initializeDateFromCalendar(): JQueryPromise<any> {
        this.dateFromCalendarOptions = {
            max: new Date(new Date().getTime()),
            bindingOptions: {
                disabled: '$ctrl.dateFromCalendarDisabled',
                value: '$ctrl.dateFromCalendarValue'
            },
            cellTemplate: (e: any) => this.getCellTemplate(e),

            onInitialized: (e: any) => {
                this.dateFromCalendar = e.component
            },
            onValueChanged: (e: any) => {
                if (!this.waitAfterDateChange) {
                    this.updateDateSelection(e.value, 1)
                }
            }
        }

        return super.waitForOnInitialized(this.dateFromCalendarOptions)
    }

    private initializeDateToCalendar(): JQueryPromise<any> {
        this.dateToCalendarOptions = {
            max: new Date(new Date().getTime()),
            bindingOptions: {
                disabled: '$ctrl.dateToCalendarDisabled',
                value: '$ctrl.dateToCalendarValue'
            },
            cellTemplate: (e: any) => this.getCellTemplate(e),

            onInitialized: (e: any) => {
                this.dateToCalendar = e.component
            },
            onValueChanged: (e: any) => {
                if (!this.waitAfterDateChange) {
                    this.updateDateSelection(e.value, -1)
                }
            }
        }

        return super.waitForOnInitialized(this.dateToCalendarOptions)
    }

    private initializePopup(): JQueryPromise<any> {
        this.popupOptions = {
            innerOptions: {
                dragEnabled: false,
                showTitle: false,
                shading: false,
                animation: null,
                maxHeight: this.popupHeight,
                maxWidth: this.popupWidth,
                minWidth: 600,
                closeOnOutsideClick: (e: any) => {
                    return $(`#${this.popupRootElementId}`).get()[0].outerHTML.indexOf(e.target.outerHTML) === -1
                },
                position: () => {
                    return this.updatePopupPosition()
                },
            },
            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.popup = e.component
            },
            // bottomToolbarOptions: {
            //     items: [
            //         {
            //             location: 'after',
            //             widget: 'dxButton',
            //             options: {
            //                 text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Ok),
            //                 hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Ok),
            //                 onClick: () => {
            //                     this.popup.hide()
            //                 }
            //             }
            //         }, {
            //             location: 'after',
            //             widget: 'dxButton',
            //             options: {
            //                 text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
            //                 hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
            //                 onClick: () => {
            //                     this.popup.hide()
            //                 },
            //             }
            //         }
            //     ]

            // }
        }

        return super.waitForOnInitialized(this.periodListOptions)
    }

    private initializePeriodChangeButtonOptions(): JQueryPromise<any> {
        this.periodChangeButtonOptions = {
            onInitialized: (e: any) => {
                this.periodChangeButton = e.component
                this.updateSelectPeriodButtonText(this.selectedPeriod.text)
                this.updateDateSelection(null, -1)
            },
            onClick: (e: any) => {
                this.popup.toggle(!this.popup.isVisible())
            }
        }

        return super.waitForOnInitialized(this.periodChangeButtonOptions)
    }

    private initializePrevButtonOptions(): JQueryPromise<any> {
        this.prevButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.PREV, this.userSession),
            hint: LocalizationHelper.getLocalizedText('PreviousTimePeriod', { collection: 'Grid' }),
            onClick: e => {
                this.getNextPrevPeriod(false)
            }
        }

        return super.waitForOnInitialized(this.prevButtonOptions)
    }

    private initializeNextButtonOptions(): JQueryPromise<any> {
        this.nextButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.NEXT, this.userSession),
            hint: LocalizationHelper.getLocalizedText('NextTimePeriod', { collection: 'Grid' }),
            onClick: e => {
                this.getNextPrevPeriod(true)
            }
        }

        return super.waitForOnInitialized(this.nextButtonOptions)
    }

    //#region Utils

    private updatePopupPosition(): DevExpress.positionConfig {
        const popupRootElement: JQuery<HTMLElement> = angular.element(`#${this.popupRootElementId}`)
        const popupElementPosition: JQuery.Coordinates = popupRootElement.offset()
        const windowWidth = window.innerWidth

        let x: number = 0
        let y: number = this.popupHeight / 2 + popupRootElement.height() / 2

        const widthOnLeft: number = popupElementPosition.left + popupRootElement.width() / 2
        const widthonRight: number = windowWidth - popupElementPosition.left - popupRootElement.width() / 2

        const popupWidth: number = this.getCurrentPopupWidth()        

        if (windowWidth > popupWidth) {
            if (widthonRight < popupWidth / 2) {
                x = -(popupWidth / 2 - popupRootElement.width() / 2)
            }

            if (widthOnLeft < popupWidth / 2) {
                x = (popupWidth / 2 - popupRootElement.width() / 2)
            }
        }

        const position: DevExpress.positionConfig = {
            of: `#${this.popupRootElementId}`,
            offset: {
                x: x,
                y: y
            }
        }

        return position
    }

    private updateSelectPeriodButtonText(text: string): void {
        this.periodChangeButton.option({ text: text })
        this.dateToCalendarDisabled = text == this.periods.all
        this.dateFromCalendarDisabled = text == this.periods.all

        if (text == this.periods.all || text == this.periods.custom) {
            this.dateFromCalendarValue = null
            this.dateToCalendarValue = null
        }
    }

    /**true is next, false is prev */
    private getNextPrevPeriod(next: boolean): void {
        let selectedIndex: number = 0

        if (_.size(this.listItems) > 0) {
            _.forEach(this.listItems, (item: dxListItem, index: number) => {
                if (item == this.selectedPeriod) {
                    selectedIndex = index
                }
            })

            if (next) {
                if (selectedIndex === _.size(this.listItems) - 1) {
                    selectedIndex = 0
                } else {
                    selectedIndex++
                }
            } else {
                if (selectedIndex === 0) {
                    selectedIndex = _.size(this.listItems) - 1
                } else {
                    selectedIndex--
                }
            }

            this.selectedPeriod = this.listItems[selectedIndex]

            if (this.periodList == null) {
                this.updateSelectPeriodButtonText(this.selectedPeriod.text)
                this.updateDateSelection(null, -1)
            }
        }
    }

    private addDays(date: Date, days: number): Date {
        const result: Date = new Date(date)
        result.setHours(0, 0, 0, 0)
        result.setDate(result.getDate() + days)
        return result
    }

    private addMonths(date: Date, months: number): Date {
        const result: Date = new Date(date)
        result.setHours(0, 0, 0, 0)
        result.setMonth(result.getMonth() + months)
        result.setDate(result.getDate() - 1)
        return result
    }

    private addYears(date: Date, years: number): Date {
        const result: Date = new Date(date)
        result.setHours(0, 0, 0, 0)
        result.setFullYear(result.getFullYear() + years)
        result.setDate(result.getDate() - 1)
        return result
    }


    private getCellTemplate(data: { date: Date, text: string, view: 'century' | 'decade' | 'month' | 'year' }) {
        if (this.dateFromCalendarValue != null && this.dateToCalendarValue != null) {
            if (data.date >= this.dateFromCalendarValue && data.date <= this.dateToCalendarValue) {
                const cssStyle: string = 'color: blue;font-weight: bold;'
                return "<span style='" + cssStyle + "'>" + data.text + "</span>"
            }
        }

        return data.text
    }

    private updateDateSelection(date: Date, direction: 1 | -1): void {
        if (this.selectedPeriod != null) {
            const dateToday: Date = new Date()
            dateToday.setHours(0, 0, 0, 0)

            if (direction === 1) {
                this.dateFromCalendarValue = date != null ? date : dateToday
            } else {
                this.dateToCalendarValue = date != null ? date : dateToday
            }

            switch (this.selectedPeriod.text) {
                case this.periods.day:
                    if (direction === 1) {
                        this.dateToCalendarValue = this.dateFromCalendarValue
                    } else {
                        this.dateFromCalendarValue = this.dateToCalendarValue
                    }
                    break
                case this.periods.week:
                    if (direction === 1) {
                        this.dateToCalendarValue = this.addDays(this.dateFromCalendarValue, 6 * direction)
                    } else {
                        this.dateFromCalendarValue = this.addDays(this.dateToCalendarValue, 6 * direction)
                    }
                    break
                case this.periods.month:
                    if (direction === 1) {
                        this.dateToCalendarValue = this.addMonths(this.dateFromCalendarValue, 1 * direction)
                    } else {
                        this.dateFromCalendarValue = this.addMonths(this.dateToCalendarValue, 1 * direction)
                    }
                    break
                case this.periods.year:
                    if (direction === 1) {
                        this.dateToCalendarValue = this.addYears(this.dateFromCalendarValue, 1 * direction)
                    } else {
                        this.dateFromCalendarValue = this.addYears(this.dateToCalendarValue, 1 * direction)
                    }
                    break
                case this.periods.custom:
                    if (date == null) {
                        this.dateFromCalendarValue = null
                        this.dateToCalendarValue = null
                    }
                    break
                case this.periods.all:
                    this.dateFromCalendarValue = null
                    this.dateToCalendarValue = null
                    break
            }

            this.repaintCalendars()
            this.onDateRangeChanged()
        }
    }

    private repaintCalendars(): void {
        if (this.dateToCalendar != null) {
            this.dateToCalendar.repaint()
        }

        if (this.dateFromCalendar != null) {
            this.dateFromCalendar.repaint()
        }

        this.waitAfterDateChange = true

        setTimeout(() => {
            this.waitAfterDateChange = false
        }, 200)
    }

    private getCurrentPopupWidth(): number {
        let popupWidthProp: number | Function = this.popup.getInnerPopup().option('width')
        let popupWidth: number

        if (_.isFunction(popupWidthProp)) {
            popupWidth = popupWidthProp()
        } else {
            popupWidth = popupWidthProp
        }

        if (this.popupWidth < popupWidth) {
            popupWidth = this.popupWidth
        }

        return popupWidth
    }


    //#endregion

}